import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "staff" | "viewer" | "super_admin";
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, isLoading, hasRole, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Plain admins carve out access to any requiredRole gate EXCEPT super_admin —
  // this is the only thing granting admins access to the 17 requiredRole="staff"
  // routes today (hasRole is exact-match, not hierarchical; see AuthContext.tsx).
  // A super_admin gate must never be satisfied by a plain admin. See #502.
  const adminCarveOut = requiredRole !== "super_admin" && hasRole("admin");

  if (requiredRole && !isSuperAdmin && !hasRole(requiredRole) && !adminCarveOut) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2">
        <h1 className="font-display text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">You don't have permission to view this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}
